import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  FormsModule,
  Validators,
} from '@angular/forms';
import { AuthService } from 'src/app/auth.service';
import { FirebaseService } from 'src/app/services/firebase.service';
import { Router } from '@angular/router';
import { Timestamp } from '@angular/fire/firestore';

@Component({
  selector: 'app-admin-panel',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule],
  templateUrl: './admin-panel.component.html',
  styleUrl: './admin-panel.component.css',
})
export class AdminPanelComponent implements OnInit {
  user: any;

  // Forms
  brandForm!: FormGroup;
  orgForm!: FormGroup;
  managerForm!: FormGroup;

  // Data
  brands: any[] = [];
  organizations: any[] = [];
  selectedOrgId: string = '';
  loginHistory: any[] = [];
  lastLoginDoc: any = null;
  loadingMore = false;

  // UI States
  activeTab: 'brands' | 'orgs' | 'managers' | 'switch' | 'logins' = 'brands';
  loading = false;
  saving = false;
  cleaningUp = false;

  // Toast
  showToast = false;
  toastMessage = '';
  toastType: 'success' | 'error' = 'success';
  orgName: string | undefined;

  constructor(
    private auth: AuthService,
    private fb: FormBuilder,
    private firebaseService: FirebaseService,
    private router: Router,
  ) {}

  async ngOnInit(): Promise<void> {
    this.user = this.auth.getUser();

    // Check if user is admin
    if (!this.user || this.user.role !== 'admin') {
      this.displayToast('Access denied. Admin privileges required.', 'error');
      this.router.navigate(['/login']);
      return;
    }
    this.loadOrgName();
    this.initializeForms();
    await this.loadData();
  }

  /**
   * Initialize all forms
   */
  private initializeForms(): void {
    // Brand Form
    this.brandForm = this.fb.group({
      name: ['', Validators.required],
      active: [true],
    });

    // Organization Form
    this.orgForm = this.fb.group({
      name: ['', Validators.required],
      brandId: ['', Validators.required],
      location: ['', Validators.required],
      active: [true],
    });

    // Manager Form
    this.managerForm = this.fb.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(6)]],
      pin: ['', [Validators.required, Validators.pattern(/^[0-9]{4,6}$/)]],
      orgId: ['', Validators.required],
    });
  }

  /**
   * Load brands, organizations, and login history
   */
  private async loadData(): Promise<void> {
    this.loading = true;
    try {
      const { logs, lastDoc } = await this.firebaseService.getLoginHistory();
      this.loginHistory = logs.map((log) => ({
        ...log,
        readableTime: this.formatTimestamp(log.timestamp),
      }));
      this.lastLoginDoc = lastDoc;
      this.brands = await this.firebaseService.getAllBrands();
      this.organizations = await this.firebaseService.getAllOrganizations();

      // console.log('✅ Data loaded successfully');
    } catch (error) {
      console.error('❌ Error loading data:', error);
      this.displayToast('Error loading data.', 'error');
    } finally {
      this.loading = false;
    }
  }

  /**
   * Create new brand
   */
  async createBrand(): Promise<void> {
    if (this.brandForm.invalid) {
      this.brandForm.markAllAsTouched();
      return;
    }

    this.saving = true;
    try {
      const brandData = {
        name: this.brandForm.value.name.trim(),
        active: this.brandForm.value.active,
      };

      await this.firebaseService.createBrand(brandData);

      this.displayToast('Brand created successfully!', 'success');
      this.brandForm.reset({ active: true });
      await this.loadData();
    } catch (error) {
      console.error('❌ Error creating brand:', error);
      this.displayToast('Error creating brand.', 'error');
    } finally {
      this.saving = false;
    }
  }

  /**
   * Create new organization
   */
  async createOrganization(): Promise<void> {
    if (this.orgForm.invalid) {
      this.orgForm.markAllAsTouched();
      return;
    }

    this.saving = true;
    try {
      const orgData = {
        name: this.orgForm.value.name.trim(),
        brandId: this.orgForm.value.brandId,
        location: this.orgForm.value.location.trim(),
        active: this.orgForm.value.active,
      };

      await this.firebaseService.createOrganization(orgData);

      this.displayToast('Organization created successfully!', 'success');
      this.orgForm.reset({ active: true });
      await this.loadData();
    } catch (error) {
      console.error('❌ Error creating organization:', error);
      this.displayToast('Error creating organization.', 'error');
    } finally {
      this.saving = false;
    }
  }

  async cleanupRequestedOff(): Promise<void> {
    if (!this.user?.orgId) {
      this.displayToast('No organization selected.', 'error');
      return;
    }

    if (
      !confirm(
        'This will remove all past requested-off dates for employees in your organization. Continue?',
      )
    ) {
      return;
    }

    this.cleaningUp = true;
    try {
      const result = await this.firebaseService.cleanupExpiredRequestedOff(
        this.user.orgId,
      );

      if (result.errors > 0) {
        this.displayToast(
          `Cleanup complete with errors. Updated ${result.cleaned} employees. ${result.errors} failed. Check console for details.`,
          'error',
        );
      } else {
        this.displayToast(
          `Cleanup complete! Updated ${result.cleaned} out of ${result.total} employees.`,
          'success',
        );
      }
    } catch (error) {
      console.error('❌ Error cleaning up requested-off dates:', error);
      this.displayToast('Error cleaning up requested-off dates.', 'error');
    } finally {
      this.cleaningUp = false;
    }
  }

  /**
   * Create new manager for an organization
   */
  async createManager(): Promise<void> {
    if (this.managerForm.invalid) {
      this.managerForm.markAllAsTouched();
      return;
    }

    this.saving = true;
    try {
      const managerData = {
        firstName: this.managerForm.value.firstName.trim(),
        lastName: this.managerForm.value.lastName.trim(),
        Name: `${this.managerForm.value.firstName.trim()} ${this.managerForm.value.lastName.trim()}`,
        email: this.managerForm.value.email.trim().toLowerCase(),
        password: this.managerForm.value.password,
        Pin: this.managerForm.value.pin,
        orgId: this.managerForm.value.orgId,
        role: 'manager',
        keyHolder: true,
        fullTime: true,
        cashier: false,
        availability: {},
        requestedOff: [],
        notes: '',
      };

      await this.firebaseService.createEmployee(managerData);

      this.displayToast('Manager created successfully!', 'success');
      this.managerForm.reset();
    } catch (error) {
      console.error('❌ Error creating manager:', error);
      this.displayToast('Error creating manager.', 'error');
    } finally {
      this.saving = false;
    }
  }

  /**
   * Load initial login history
   */
  async loadLoginHistory(): Promise<void> {
    this.loading = true;
    try {
      const { logs, lastDoc } = await this.firebaseService.getLoginHistory();
      this.loginHistory = logs.map((log) => ({
        ...log,
        readableTime: this.formatTimestamp(log.timestamp),
      }));
      this.lastLoginDoc = lastDoc;
    } catch (error) {
      console.error('❌ Error loading login history:', error);
      this.displayToast('Error loading login history.', 'error');
    } finally {
      this.loading = false;
    }
  }

  /**
   * Load next page
   */
  async loadMoreLoginHistory(): Promise<void> {
    if (!this.lastLoginDoc) return;
    this.loadingMore = true;
    try {
      const { logs, lastDoc } = await this.firebaseService.getLoginHistory(
        50,
        this.lastLoginDoc,
      );
      const formatted = logs.map((log) => ({
        ...log,
        readableTime: this.formatTimestamp(log.timestamp),
      }));
      this.loginHistory.push(...formatted);
      this.lastLoginDoc = lastDoc;
    } catch (error) {
      console.error('❌ Error loading more logs:', error);
    } finally {
      this.loadingMore = false;
    }
  }

  /**
   * Convert Firestore timestamp to readable format
   */
  formatTimestamp(ts: any): string {
    if (!ts) return '—';
    const date = ts.seconds ? new Date(ts.seconds * 1000) : new Date(ts);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  }

  /**
   * Switch admin's active organization
   */
  async switchOrganization(): Promise<void> {
    if (!this.selectedOrgId) {
      this.displayToast('Please select an organization.', 'error');
      return;
    }

    this.saving = true;
    try {
      // Update admin's orgId
      await this.firebaseService.updateEmployeeProfile(this.user.id, {
        orgId: this.selectedOrgId,
      });

      // Update local user session
      const updatedUser = { ...this.user, orgId: this.selectedOrgId };
      await this.auth.updateUser(updatedUser);
      this.user = this.auth.getUser();

      const selectedOrg = this.organizations.find(
        (o) => o.id === this.selectedOrgId,
      );
      this.displayToast(
        `Switched to organization: ${selectedOrg?.name || 'Unknown'}`,
        'success',
      );
    } catch (error) {
      console.error('❌ Error switching organization:', error);
      this.displayToast('Error switching organization.', 'error');
    } finally {
      this.saving = false;
    }
  }

  /**
   * Set active tab
   */
  setActiveTab(
    tab: 'brands' | 'orgs' | 'managers' | 'switch' | 'logins',
  ): void {
    this.activeTab = tab;

    // Auto-load login history when switching to that tab
    if (tab === 'logins') {
      this.loadLoginHistory();
    }
  }

  /**
   * Get brand name by ID
   */
  getBrandName(brandId: string): string {
    const brand = this.brands.find((b) => b.id === brandId);
    return brand?.name || 'Unknown';
  }

  /**
   * Display toast notification
   */
  displayToast(message: string, type: 'success' | 'error'): void {
    this.toastMessage = message;
    this.toastType = type;
    this.showToast = true;
    setTimeout(() => (this.showToast = false), 4000);
  }

  closeToast(): void {
    this.showToast = false;
  }

  async loadOrgName() {
    const orgId = this.auth.getUsersOrgId();
    if (!orgId) {
      this.orgName = 'Organization';
      return;
    }

    const orgName = await this.firebaseService.getOrgNameByOrgId(orgId);
    this.orgName = orgName || 'Organization';
    // console.log('Organization loaded:', this.orgName);
  }
}
